import { Box, Flex, Stack } from "@chakra-ui/react";
import { FC, useEffect, useRef } from "react";
import {
  Control,
  FieldValues,
  UseFormResetField,
  UseFormWatch,
} from "react-hook-form";
import InputFieldComponent from "../../../components/molecule/InputField/InputFieldComponent";
import { getProvince } from "../../../services/TabelasAuxiliares/getProvince";
import { getCountyById } from "../../../services/TabelasAuxiliares/getCountyById";
import FormSelect from "../../../components/molecule/Select/FormSelect";
import { useSkipFirstRender } from "../../../hooks/useSkipFirstRender";
import RadioControlGroup from "../../../components/molecule/RadioControlGroup/RadioControlGroup";
import { getFieldOccurencesLevels } from "../../../services/FieldOccurences/getFieldOccurencesLevels";
import { getFieldOccurencesTypes } from "../../../services/FieldOccurences/getFieldOccurencesTypes";
import TextAreaComponent from "../../../components/molecule/TextArea/TextAreaComponent";

interface FieldOccurancesSubmitFormProps {
  errors: any;
  control: Control<any>;
  isDisabled?: boolean;
  resetField: UseFormResetField<FieldValues>;
  isFormInitialized?: boolean;
  watch: UseFormWatch<FieldValues>;
}

const FieldOccurancesSubmitForm: FC<FieldOccurancesSubmitFormProps> = ({
  errors,
  control,
  isDisabled,
  resetField,
  isFormInitialized = true,
  watch,
}) => {
  const selectedLevel = isFormInitialized ? watch("levelOption") : undefined;
  const selectedProvince = isFormInitialized
    ? watch("provinceOption")
    : undefined;
  const prevSelectedProvince = useRef(selectedProvince);
  const skipFirstRender = useSkipFirstRender();
  useEffect(() => {
    if (!isFormInitialized) return;

    if (skipFirstRender()) {
      return;
    }

    if (selectedLevel) {
      resetField("countyOption");
      resetField("provinceOption");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLevel]);

  useEffect(() => {
    if (prevSelectedProvince.current && !selectedProvince) {
      resetField("countyOption");
    }
    prevSelectedProvince.current = selectedProvince;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvince]);

  return (
    <Stack gap={4}>
      <RadioControlGroup
        errors={errors}
        control={control}
        fetchOptions={getFieldOccurencesTypes}
        fieldName="Tipo de ocorrência:"
        name="typeOption"
        isRequired
        isDisabled={isDisabled}
      />
      <InputFieldComponent
        label="Título"
        name="title"
        type="text"
        register={control.register}
        errors={errors}
        isRequired
        isDisabled={isDisabled}
        counter={150}
      />
      <FormSelect
        control={control}
        errors={errors}
        watch={watch}
        fieldName="levelOption"
        fieldLabel="Nível"
        fetchOptions={getFieldOccurencesLevels}
        isDisabled={isDisabled}
      />
      <Flex gap={4} direction="row" justifyContent="flex-start">
        <Box flex={1}>
          <FormSelect
            control={control}
            errors={errors}
            watch={watch}
            fieldName="provinceOption"
            fieldLabel="Província"
            fetchOptions={getProvince}
            dependencyWatchField="levelOption"
            dependencyCondition={() => selectedLevel !== null}
            isRequiredCondition={() => selectedLevel !== null}
            isDisabled={isDisabled || selectedLevel === undefined}
          />
        </Box>

        <Box flex={1}>
          <FormSelect
            control={control}
            errors={errors}
            watch={watch}
            fieldName="countyOption"
            fieldLabel="Município"
            fetchOptions={() =>
              getCountyById({ ParentId: selectedProvince?.id })
            }
            dependencyWatchField="levelOption"
            dependencyCondition={(selectedProvince) => !!selectedProvince}
            isRequiredCondition={(selected) =>
              selected?.id === 2 && selectedLevel !== null
            }
            isDisabled={
              isDisabled ||
              selectedLevel === null ||
              !selectedProvince ||
              selectedLevel?.id === 1
            }
          />
        </Box>
      </Flex>
      <TextAreaComponent
        label="Detalhes sobre a ocorrência"
        name="description"
        register={control.register}
        errors={errors}
        isRequired
        isDisabled={isDisabled}
      />
    </Stack>
  );
};

export default FieldOccurancesSubmitForm;
